import React, { useState, useEffect, useCallback } from 'react';
import {
  Layers, RefreshCw, Play, Pause, AlertTriangle,
  CheckCircle2, Clock, XCircle, Shield, Zap, TrendingUp,
  BarChart3, Target
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './DetailExtractionPanel.css';

function DetailExtractionPanel({ departmentCode = null, onViewComparison = null }) {
  const [dashboard, setDashboard] = useState(null);
  const [throttleState, setThrottleState] = useState(null);
  const [config, setConfig] = useState({ enabled: true, batch_size: 5 });
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [batching, setBatching] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    try {
      const [dash, throttle, cfg, imp] = await Promise.all([
        apiClient.get('/api/scraper/detail/dashboard').catch(() => null),
        apiClient.get('/api/scraper/throttle').catch(() => null),
        apiClient.get('/api/scraper/detail/config').catch(() => null),
        apiClient.get('/api/scraper/detail/classification-impact').catch(() => null),
      ]);
      if (dash) setDashboard(dash);
      if (throttle) setThrottleState(throttle);
      if (cfg) setConfig(cfg);
      if (imp) setImpact(imp);
    } catch (err) {
      console.error('DetailExtractionPanel fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleQueueBatch = async () => {
    setBatching(true);
    try {
      await apiClient.post(`/api/scraper/detail/batch?batch=${config.batch_size || 5}`);
      await fetchData();
    } catch (err) {
      console.error('Batch queue error:', err);
    } finally {
      setBatching(false);
    }
  };

  const handleToggleScheduler = async () => {
    const newConfig = { ...config, enabled: !config.enabled };
    try {
      await apiClient.put('/api/scraper/detail/config', newConfig);
      setConfig(newConfig);
    } catch (err) {
      console.error('Config update error:', err);
    }
  };

  const handleBatchSizeChange = async (size) => {
    const newConfig = { ...config, batch_size: size };
    try {
      await apiClient.put('/api/scraper/detail/config', newConfig);
      setConfig(newConfig);
    } catch (err) {
      console.error('Config update error:', err);
    }
  };

  const handleResetThrottle = async () => {
    try {
      const result = await apiClient.post('/api/scraper/throttle/reset');
      setThrottleState(result.state);
    } catch (err) {
      console.error('Throttle reset error:', err);
    }
  };

  if (loading) return <div className="dep-loading">Loading detail extraction status...</div>;

  const stats = dashboard?.stats || {};
  const throughput = dashboard?.throughput || {};
  const eta = dashboard?.eta || {};
  const total = parseInt(stats.total || 0);
  const pending = parseInt(stats.pending || 0);
  const queued = parseInt(stats.queued || 0);
  const complete = parseInt(stats.complete || 0);
  const failed = parseInt(stats.failed || 0);
  const completePct = total > 0 ? Math.round((complete / total) * 100) : 0;

  const isBlocked = throttleState?.blocked && new Date(throttleState?.blocked_until) > new Date();
  const blockedMinutes = isBlocked
    ? Math.max(0, Math.round((new Date(throttleState.blocked_until) - new Date()) / 60000))
    : 0;

  return (
    <div className="dep-panel">
      <div className="dep-header">
        <div className="dep-title">
          <Layers size={16} />
          <h3>PO Detail Extraction</h3>
        </div>
        <div className="dep-header-actions">
          <div className="dep-tabs">
            <button className={`dep-tab ${activeTab === 'overview' ? 'dep-tab-active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
            <button className={`dep-tab ${activeTab === 'departments' ? 'dep-tab-active' : ''}`} onClick={() => setActiveTab('departments')}>By Dept</button>
            <button className={`dep-tab ${activeTab === 'activity' ? 'dep-tab-active' : ''}`} onClick={() => setActiveTab('activity')}>Activity</button>
          </div>
          <button className="dep-refresh" onClick={fetchData} title="Refresh">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* ── Throughput + ETA Row ──────────────────────────────────── */}
      <div className="dep-throughput-row">
        <div className="dep-throughput-item">
          <TrendingUp size={13} />
          <span className="dep-throughput-value">{throughput.completed_1h || 0}</span>
          <span className="dep-throughput-label">/hour</span>
        </div>
        <div className="dep-throughput-item">
          <BarChart3 size={13} />
          <span className="dep-throughput-value">{throughput.completed_24h || 0}</span>
          <span className="dep-throughput-label">/day</span>
        </div>
        <div className="dep-throughput-item">
          <Clock size={13} />
          <span className="dep-throughput-value">{eta.hours || '?'}h</span>
          <span className="dep-throughput-label">ETA</span>
        </div>
        <div className="dep-throughput-item">
          <Target size={13} />
          <span className="dep-throughput-value">{eta.avgDurationSeconds || '?'}s</span>
          <span className="dep-throughput-label">avg</span>
        </div>
      </div>

      {/* ── Progress bar ─────────────────────────────────────────── */}
      {total > 0 && (
        <div className="dep-progress-section">
          <div className="dep-progress-bar">
            <div className="dep-progress-fill" style={{ width: `${completePct}%` }} />
          </div>
          <div className="dep-progress-label">
            {complete.toLocaleString()} / {total.toLocaleString()} POs detailed ({completePct}%)
          </div>
        </div>
      )}

      {/* ── Stats grid ───────────────────────────────────────────── */}
      <div className="dep-stats">
        <div className="dep-stat">
          <Clock size={14} />
          <span className="dep-stat-value">{pending.toLocaleString()}</span>
          <span className="dep-stat-label">Pending</span>
        </div>
        <div className="dep-stat">
          <Zap size={14} />
          <span className="dep-stat-value">{(queued).toLocaleString()}</span>
          <span className="dep-stat-label">In Queue</span>
        </div>
        <div className="dep-stat">
          <CheckCircle2 size={14} />
          <span className="dep-stat-value dep-stat-success">{complete.toLocaleString()}</span>
          <span className="dep-stat-label">Complete</span>
        </div>
        <div className="dep-stat">
          <XCircle size={14} />
          <span className="dep-stat-value dep-stat-error">{failed.toLocaleString()}</span>
          <span className="dep-stat-label">Failed</span>
        </div>
      </div>

      {/* ── Classification Impact Card ───────────────────────────── */}
      {impact && impact.couldClassifyWithDetail > 0 && (
        <div className="dep-impact-card">
          <div className="dep-impact-header">
            <Target size={14} />
            <span>Classification Impact</span>
          </div>
          <div className="dep-impact-body">
            <p><strong>{impact.couldClassifyWithDetail}</strong> items could gain free classification from detail data</p>
            {impact.unclassifiedSpend > 0 && (
              <p className="dep-impact-spend">${(impact.unclassifiedSpend / 1000000).toFixed(2)}M unclassified spend with detail data</p>
            )}
          </div>
        </div>
      )}

      {/* ── Priority Distribution ────────────────────────────────── */}
      {dashboard?.priorityDistribution?.length > 0 && (
        <div className="dep-priority-bar">
          <div className="dep-priority-label">Priority Queue</div>
          <div className="dep-priority-track">
            {dashboard.priorityDistribution.map(band => {
              const pct = pending > 0 ? (parseInt(band.count) / pending * 100) : 0;
              return (
                <div
                  key={band.band}
                  className={`dep-priority-segment dep-priority-${band.band}`}
                  style={{ width: `${Math.max(pct, 2)}%` }}
                  title={`${band.band}: ${band.count} items`}
                />
              );
            })}
          </div>
          <div className="dep-priority-legend">
            {dashboard.priorityDistribution.map(band => (
              <span key={band.band} className={`dep-priority-legend-item dep-priority-${band.band}`}>
                {band.band}: {parseInt(band.count).toLocaleString()}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Tab Content ──────────────────────────────────────────── */}
      {activeTab === 'departments' && dashboard?.byDepartment?.length > 0 && (
        <div className="dep-dept-table">
          <table>
            <thead>
              <tr>
                <th>Dept</th>
                <th>Pending</th>
                <th>Complete</th>
                <th>Failed</th>
                <th>Priority</th>
                <th>Progress</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.byDepartment.map((dept, i) => {
                const dTotal = parseInt(dept.complete || 0) + parseInt(dept.pending || 0) + parseInt(dept.failed || 0);
                const dPct = dTotal > 0 ? Math.round(parseInt(dept.complete || 0) / dTotal * 100) : 0;
                return (
                  <tr key={i}>
                    <td className="dep-dept-code">{dept.department_code || '—'}</td>
                    <td>{parseInt(dept.pending || 0).toLocaleString()}</td>
                    <td className="dep-stat-success">{parseInt(dept.complete || 0).toLocaleString()}</td>
                    <td className="dep-stat-error">{parseInt(dept.failed || 0).toLocaleString()}</td>
                    <td>{dept.avg_priority || '—'}</td>
                    <td>
                      <div className="dep-mini-bar">
                        <div className="dep-mini-fill" style={{ width: `${dPct}%` }} />
                        <span>{dPct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'activity' && dashboard?.recentJobs?.length > 0 && (
        <div className="dep-recent">
          <div className="dep-recent-list">
            {dashboard.recentJobs.slice(0, 12).map((job, i) => {
              const params = typeof job.params === 'string' ? JSON.parse(job.params) : job.params;
              return (
                <div key={i} className={`dep-recent-item dep-recent-${job.status}`}
                  onClick={() => onViewComparison && params?.documentNumber && onViewComparison(params.documentNumber)}
                  style={onViewComparison ? { cursor: 'pointer' } : {}}
                  title={onViewComparison ? 'Click to compare list vs detail data' : ''}>
                  <span className="dep-recent-doc">{params?.documentNumber?.substring(0, 18) || '?'}</span>
                  <span className="dep-recent-duration">{job.duration_s ? `${job.duration_s}s` : '—'}</span>
                  <span className="dep-recent-lines">{job.line_items_count || 0} lines</span>
                  <span className={`dep-recent-status dep-status-${job.status}`}>
                    {job.status}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Throttle status ──────────────────────────────────────── */}
      {isBlocked && (
        <div className="dep-throttle dep-throttle-blocked">
          <div className="dep-throttle-header">
            <Shield size={14} />
            <span>Site Blocking Detected</span>
          </div>
          <div className="dep-throttle-detail">
            <p>CaleProcure returning errors. Auto-paused for {blockedMinutes} min.</p>
            <button className="dep-btn dep-btn-sm" onClick={handleResetThrottle}>
              Reset Throttle
            </button>
          </div>
        </div>
      )}

      {/* ── Controls ─────────────────────────────────────────────── */}
      <div className="dep-controls">
        <div className="dep-control-row">
          <span className="dep-control-label">Auto-Schedule</span>
          <button
            className={`dep-toggle ${config.enabled ? 'dep-toggle-on' : 'dep-toggle-off'}`}
            onClick={handleToggleScheduler}
          >
            {config.enabled ? <Play size={12} /> : <Pause size={12} />}
            {config.enabled ? 'On' : 'Off'}
          </button>
        </div>
        <div className="dep-control-row">
          <span className="dep-control-label">Batch Size</span>
          <div className="dep-batch-sizes">
            {[3, 5, 10, 20].map(size => (
              <button
                key={size}
                className={`dep-batch-btn ${config.batch_size === size ? 'dep-batch-btn-active' : ''}`}
                onClick={() => handleBatchSizeChange(size)}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
        <button
          className="dep-btn dep-btn-primary"
          onClick={handleQueueBatch}
          disabled={batching || pending === 0 || isBlocked}
        >
          {batching ? <RefreshCw size={14} className="dep-spin" /> : <Layers size={14} />}
          {batching ? 'Queuing...' : `Queue ${config.batch_size || 5} Detail Jobs`}
        </button>
      </div>
    </div>
  );
}

export default DetailExtractionPanel;
